import React, {useEffect, useState} from 'react';
import Modal from "react-bootstrap/Modal";
import {
    Button, Container, Form, FormControl, FormGroup, FormLabel, Row, Col,
} from "react-bootstrap";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';
import LinearProgress from "@mui/material/LinearProgress";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {useAppInsightsContext} from "@microsoft/applicationinsights-react-js";
import {isMobile} from "react-device-detect";
import { BsExclamationTriangleFill } from 'react-icons/bs';
import getBrandName from "app/shared/utils/BrandHelper";
import {
    useGetUserDetailsMutation,
    useGuestMutation,
    useLoginMutation,
    useSubmitFinalOrderMutation, useUpdateFinalOrderMutation,
} from "app/services/api/mutateGateAPI";
import {
setCredentials, toggleCartDrawer, setResetPasswordSuccessMsg,
setForgotPasswordMsg, setPreferencesModalOpen, setAlreadySignUpErrorMsg,
setRedirectToMyAccount, setReirectToRewardsHome,
setRedirectToGiftCardTransfer,
setShowGlobalAlert,
setForceLogoutErrorMsg,
setRedirectToAccountNotifications,
} from "app/store/slices/authSlice";
import {updateUserDetail} from "app/store/slices/userSlice";
import {decrypt, deleteKeyPersist, setKeyPersist} from "app/shared/utils/EncryptionUtility";
import logging from "app/shared/utils/logging";
import {GuestRequest, GuestResponse, GuestResponseType} from 'app/features/Guest/types/Guest';
import {
    changeOrderNumber,
    changeOrderStatus,
    setLoading,
} from 'app/store/slices/orderSlice';
import { FinalOrderResponse, FinalOrderResponseType } from 'app/features/Order/types/FinalOrderResponseType';
import { OrderSubmitType } from 'app/features/Order/types/OrderSubmitType';
import ForgotPasswordForm from 'app/features/MyAccount/ForgotPassword/ForgotPasswordForm';
import NotificationBanner from 'app/shared/components/NotificationBanner';
import SignUpDetails from 'app/features/MyAccount/SignUpDetails';
import SignupHeader from 'app/features/MyAccount/SignupHeader';
import SignupFooter from 'app/features/MyAccount/SignupFooter';
import GRecaptcha from 'app/features/GRecaptcha/GRecaptcha';
import gRecaptchaManager from 'app/features/GRecaptcha/GRecaptchaManager';
import { Routes } from 'app/shared/constants/constants';
import {LoginRequest, LoginResponse, LoginResponseType} from "./types/AuthResponseType";
import {UserDetailResponse, UserDetailResponseType} from "./types/UserDetailResponseType";
import {RootState} from '../../store';
import useGA4Layer from '../../shared/hooks/useGA4Layer';

type Props = {
    isOpen: boolean,
    isTStore?: boolean,
    onGuestUserReady?: () => void,
    hideModal: () => void,
    isEmbedMobileGiftCardTransfer?: boolean
};

const LoginView: React.FC<Props> = ({
    isOpen,
    hideModal,
    isEmbedMobileGiftCardTransfer,
    isTStore,
    onGuestUserReady,
}) => {
    const appInsights = useAppInsightsContext();
    const ga4Layer = useGA4Layer();
    const metricData = {
        average: 5,
        name: "React Component Engaged Time (seconds)",
        sampleCount: 1,
    };
    const additionalProperties = {"Component Name": 'LocationPage'};
    appInsights.trackMetric(metricData, additionalProperties);
    const navigate = useNavigate();
    const {
        user,
        isGuest,
        resetPasswordSuccessMsg,
        alreadySignUpErrorMsg,
        redirectToMyAccount,
        redirectToGiftCardTransfer,
        redirectToRewardsHome,
        redirectToAccountNotifications,
        forceLogoutErrorMsg,
    } = useSelector((state: RootState) => state.auth);
    const {
        finalOrder,
        orderNumber,
        enterpriseUnitId,
    } = useSelector((state: RootState) => state.orders);
    const {
        storeNumber,
        storeId,
        selectedStore,
    } = useSelector((state: RootState) => state.stores);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [validated, setValidated] = useState(false);
    const [modalWidthClass, setModalWidthClass] = useState('');
    const [showSignupModal, setShowSignupModal] = useState(false);
    const [loginFailureMessage, setLoginFailureMessage] = useState("");
    const [guestLoginFailureMessage, setGuestLoginFailureMessage] = useState("");
    const [isValidEmail, setIsValidEmail] = useState(false);
    const [isInvalidEmail, setIsInvalidEmail] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isPassInvalid, setIsPassInvalid] = useState(false);
    const [showGuest, setShowGuest] = useState(true);
    const [forgotPasswordClicked, setForgotPasswordClicked] = useState(false);
    const {
        rewardsLogoPath, programPreference, rewardsText,
    } = getBrandName();
    const [login, {isLoading}] = useLoginMutation();
    const [getUserDetails, {isLoading: userDataLoading}] = useGetUserDetailsMutation();
    const [guest] = useGuestMutation();
    const uniqueId = uuidv4();
    const dispatch = useDispatch();
    const [submitFinalOrder] = useSubmitFinalOrderMutation();
    const [updateFinalOrder] = useUpdateFinalOrderMutation();

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            const signInBtn = document.getElementById('submitBtn');
            if (signInBtn) {
                signInBtn.click();
            }
        }
    };

    const populateOrderPreValidate = async (orderBody: OrderSubmitType, isTransition = false) => {
        try {
            dispatch(toggleCartDrawer(false));
            dispatch(setLoading(true));
            let response: FinalOrderResponse;
            if (orderNumber && enterpriseUnitId && !isTransition) {
                response = await updateFinalOrder({
                    orderBody,
                    orderNumber: orderNumber || '',
                }).unwrap();
            } else {
                const {data} = orderBody;
                response = await submitFinalOrder({...orderBody, data: { ...data, Status: "ReadyForValidation" }}).unwrap();
            }
            const tempValue: string = decrypt(response?.value?.toString());
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const preValidate: FinalOrderResponseType = JSON.parse(tempValue);
            logging.info(appInsights, "_PRE_VALIDATE_ORDER_RESP", "Pre Validation Response", preValidate);
            dispatch(changeOrderStatus(preValidate));
            dispatch(changeOrderNumber(preValidate?.check_number));
            navigate(`/confirmation/${preValidate?.check_number}`);
            dispatch(setLoading(false));
        } catch (e) {
            dispatch(setLoading(false));
            logging.error(appInsights, "_PRE_VALIDATE_ORDER_ERR", "Error placing order", e);
            throw e;
        }
    };

    const createOrderIfExists = (isTransitionRequired = false) => {
        if (Object.keys(finalOrder).length > 0) {
            let modifiedOrderBody = {...finalOrder};
            if (user && isGuest) {
                modifiedOrderBody = {...finalOrder, reward_ids: []};
            }
            logging.info(appInsights, "_PRE_VALIDATE_ORDER_REQ", "Final order body", finalOrder);
            populateOrderPreValidate(modifiedOrderBody, isTransitionRequired).then(() => {
                logging.debug("PreOrderCompleted", " Order was validated");
            }).catch((e: string) => {
                dispatch(setLoading(false));
                logging.debug("populateOrderPreValidate Failed", e);
            });
        }
    };

    const handleGuestCheck = async (deviceId: string) => {
        const guestBody: GuestRequest = {
            deviceId,
        };
        setLoginFailureMessage('');
        dispatch(setResetPasswordSuccessMsg(""));
        dispatch(setForceLogoutErrorMsg(null));
        try {
            appInsights.trackEvent({
                name: 'evt_GuestRegisterDevice_req',
                properties: { // accepts any type
                    bodyValue: deviceId,
                    user: deviceId,
                    trace_id: localStorage.getItem('trace_id'),
                },
            });
            const response: GuestResponseType = await guest(guestBody).unwrap();
            const tempValue: string = decrypt(response?.value?.toString());
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const data: GuestResponse = JSON.parse(tempValue);
            logging.info(appInsights, 'evt_Guest_checkin_resp', "GUEST_CHECKIN_SUCCESS", data);
            deleteKeyPersist('user');
            deleteKeyPersist('token');
            deleteKeyPersist('isGuest');
            dispatch(setCredentials({user: data?.aud, token: data?.token, isGuest: true}));
            hideModal();
            setGuestLoginFailureMessage('');
            return data;
        } catch (e) {
            setGuestLoginFailureMessage("We have run into a problem! Kindly reload or open page in a new window");
            logging.error(appInsights, 'evt_Guest_checkin_err', "GUEST_CHECKIN_FAILED", e);
            return null;
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    useEffect(() => {
        setIsValidEmail(false);
        setIsInvalidEmail(false);
        setGuestLoginFailureMessage('');
        setEmail('');
        setPassword('');
        setLoginFailureMessage('');
    }, [isOpen]);

    useEffect(() => {
        if ((storeNumber !== '' || storeId !== '')) {
            if (selectedStore?.c_guestCheckout) {
                setShowGuest(true);
            } else {
                setShowGuest(false);
            }
        }
        // eslint-disable-next-line
    }, [storeNumber, storeId, storeNumber]);

    const populateUserDetails = async (aud: string) => {
        try {
            const response: UserDetailResponse = await getUserDetails({uid: aud}).unwrap();
            const tempValue: string = decrypt(response.value.toString());
            // eslint-disable-next-line
            const userDetails: UserDetailResponseType = JSON.parse(tempValue)

            if (userDetails?.getResult?.preferences
                && !userDetails.getResult.preferences[programPreference]) {
                // user is logged-in but not enrolled, make them enroll
                hideModal();
                dispatch(setPreferencesModalOpen(true));

                return false;
            }

            dispatch(updateUserDetail(userDetails));
            return true;
        } catch (e) {
            logging.error(appInsights, "_LOGIN_ERR", "error populating user details", e);
            return false;
        }
    };

    const handleSubmitCredentials = async (emailValue: string, passwordValue: string) => {
        // eslint-disable-next-line max-len
        const recaptchaToken = await gRecaptchaManager.executeAndRetrieveToken(appInsights, "LOGIN") as string;

        setLoginFailureMessage("");
        setGuestLoginFailureMessage("");
        if (emailValue && passwordValue) {
            const creds: LoginRequest = {
                username: emailValue,
                password: passwordValue,
                recaptchaToken,
            };

            try {
                logging.info(appInsights, 'evt_RewardLogin_req', "LOGIN_SUBMIT", creds.username);
                const response: LoginResponseType = await login(creds).unwrap();
                const tempValue: string = decrypt(response.value.toString());
                // eslint-disable-next-line
                const data: LoginResponse = JSON.parse(tempValue);
                let isTransitionRequired = false;

                appInsights.trackEvent({
                    name: 'evt_RewardLogin_resp',
                    properties: { // accepts any type
                        bodyValue: data,
                        user: creds.username,
                        trace_id: localStorage.getItem('trace_id'),
                    },
                });
                logging.info(appInsights, 'evt_RewardLogin_resp', "LOGIN_SUCCESS", data.aud);
                if (!!user && isGuest) {
                    isTransitionRequired = true;
                }
                setKeyPersist('user', data.aud);
                setKeyPersist('isGuest', "false");
                dispatch(setCredentials({user: data.aud, token: "", isGuest: false}));
                ga4Layer.pushOtherEvent("login");
                dispatch(setShowGlobalAlert(true));
                const isCompleted = await populateUserDetails(data.aud);
                if (isCompleted) {
                    createOrderIfExists(isTransitionRequired);
                }
                setEmail("");
                setPassword("");
                hideModal();
                /* for the giftcard transfer deep link, on signing in
                - redirect the user to gift card transfer page */
                if (redirectToGiftCardTransfer) {
                    dispatch(setRedirectToGiftCardTransfer(!redirectToGiftCardTransfer));
                    navigate(Routes.WEB_GIFTCARD_TRANSFER_ROUTE);
                } else if (redirectToAccountNotifications) {
                    dispatch(setRedirectToAccountNotifications(false));
                    navigate(Routes.MY_ACCOUNT_NOTIFICATIONS_ROUTE);
                } else
                //for the myaccount deep link, on signing in - redirect the user to myaccount page
                if (redirectToMyAccount) {
                    dispatch(setRedirectToMyAccount(!redirectToMyAccount));
                    navigate("/myaccount");
                } else if (redirectToRewardsHome) {
                    dispatch(setReirectToRewardsHome(!redirectToMyAccount));
                    navigate("/rewards");
                }
            } catch (e) {
                logging.error(appInsights, 'evt_RewardLogin_err', "LOGIN_FAILED");
                setIsValidEmail(false);
                setLoginFailureMessage("Login Failed!");
            }
        }
    };

    const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        // clear already sign up error message
        dispatch(setAlreadySignUpErrorMsg(null));
        dispatch(setForceLogoutErrorMsg(null));

        const regex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
        if (!email || !regex.test(email)) {
            setIsInvalidEmail(true);
            setIsValidEmail(false);
            return false;
        }
        setIsInvalidEmail(false);
        setIsValidEmail(true);

        if (!password) {
            setIsPassInvalid(true);
            return false;
        }
        setIsPassInvalid(false);

        setValidated(true);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        handleSubmitCredentials(email, password).then((r) => {
            logging.debug("_LOGIN_COMPLETE", "Login is completed", r);
        });
        return true;
    };

    const handleGuest = async (event: React.MouseEvent<HTMLButtonElement>) => {
        setGuestLoginFailureMessage('');
        event.preventDefault();
        event.stopPropagation();
        const data = await handleGuestCheck(uniqueId);
        if (isTStore && onGuestUserReady && data) {
            onGuestUserReady();
            return;
        }
        setTimeout(() => {
            if (data) {
                createOrderIfExists();
            }
        }, 2000);
    };

    const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setLoginFailureMessage("");
        setGuestLoginFailureMessage("");
        setEmail(event.currentTarget.value);
    };

    const onPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setLoginFailureMessage("");
        setGuestLoginFailureMessage("");
        setPassword(event.currentTarget.value);
    };

    const toggleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const openSignUpModal = () => {
        setShowSignupModal(true);
        setModalWidthClass("modal-90w");
        dispatch(setAlreadySignUpErrorMsg(null));
        dispatch(setResetPasswordSuccessMsg(""));
        dispatch(setForceLogoutErrorMsg(null));
    };

    return (
        <div className="form-container">
            <Form noValidate validated={validated}>
                <Modal
                    style={{zIndex: "9999"}}
                    show={isOpen}
                    onHide={() => {
                        dispatch(setAlreadySignUpErrorMsg(null));
                        setLoginFailureMessage("");
                        setModalWidthClass("");
                        if (forgotPasswordClicked) {
                            dispatch(setForgotPasswordMsg(""));
                            setForgotPasswordClicked(false);
                        } else if (showSignupModal) {
                            setShowSignupModal(false);
                        } else {
                            hideModal();
                        }
                    }}
                    centered
                    keyboard={false}
                    backdrop="static"
                    dialogClassName={!isMobile ? modalWidthClass : ""}
                >
                    {forgotPasswordClicked ? (
                        <>
                            <Modal.Header closeButton className="border-bottom-0" />
                            <Modal.Body className="bb-pt-0">
                            {isOpen && <GRecaptcha/>}
                                <ForgotPasswordForm />
                            </Modal.Body>
                        </>
                    )
                    : (
                        <>
                            {showSignupModal && (
                                <Modal.Header closeButton className="border-bottom-none"/>
                            )}
                            {!showSignupModal && (
                                    <Modal.Header className="login-view-header">
                                        <Container className="d-flex align-items-center flex-column p-md-0">
                                        <img
                                            className="login-logo"
                                            src={rewardsLogoPath}
                                            alt="Login to Proceed to Checkout Pop-up"
                                        />
                                        {alreadySignUpErrorMsg && (
                                            <Row>
                                                <Col className="text-center p-0">
                                                    <p className="font-main mt-2">Welcome back!</p>
                                                    <p className="font-sub font-15">{`It looks like you already have an ${rewardsText} account.`}</p>
                                                    <p className="font-sub font-15">Please sign in below.</p>
                                                </Col>
                                            </Row>
                                        )}
                                        {isLoading && <LinearProgress className="linear-progress"/>}
                                        </Container>
                                    </Modal.Header>
                            )}
                                <Modal.Body className={`${showSignupModal ? "bb-pt-0" : "login-view-body"}`}>
                                {isOpen && <GRecaptcha key={password}/>}
                                {showSignupModal && (
                                    <>
                                        <SignupHeader/>
                                        {isLoading && <LinearProgress className="linear-progress"/>}

                                        <SignUpDetails/>
                                    </>
                                )}
                                {resetPasswordSuccessMsg
                                && (
                                    <Row className="d-flex align-items-center flex-column mx-1 mb-3">
                                        <Col className="text-center w-auto">
                                            <NotificationBanner message={resetPasswordSuccessMsg} type="success" onClose={() => dispatch(setResetPasswordSuccessMsg(""))} />
                                        </Col>
                                    </Row>
                                )}
                                {!showSignupModal && (
                                    <>
                                        <FormGroup className="mb-3">
                                            <FormLabel className="signOn-subText">Email address</FormLabel>
                                            <FormControl
                                                id="login-input-email"
                                                title="login-input-email"
                                                className="login-input"
                                                aria-required
                                                isValid={isValidEmail}
                                                isInvalid={isInvalidEmail}
                                                type="email"
                                                value={email}
                                                onChange={onEmailChange}
                                                placeholder="example@example.com"
                                            />
                                            <FormControl.Feedback type="invalid">
                                                <BsExclamationTriangleFill size={16} className="errorImage m-0" />
                                                <span className="font-sub errorText">Incorrect email</span>
                                            </FormControl.Feedback>

                                            <FormControl.Feedback className="font-sub login-success">Looks good!</FormControl.Feedback>
                                        </FormGroup>

                                        <FormGroup className="mb-3 password-wrapper">
                                            <FormLabel className="signOn-subText">Password</FormLabel>
                                            <FormControl
                                                id="login-input-password"
                                                title="login-input-password"
                                                className="login-input"
                                                aria-required
                                                isInvalid={isPassInvalid}
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={onPasswordChange}
                                                placeholder="Password"
                                            />
                                            <div
                                                role="button"
                                                className="password-toggle-button"
                                                aria-labelledby="login-input-password"
                                                tabIndex={0}
                                                onClick={toggleShowPassword}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter" || event.key === " ") {
                                                            event.preventDefault();
                                                            event.stopPropagation();
                                                            toggleShowPassword();
                                                        }
                                                    }}
                                            >
                                                {showPassword ? (
                                                        <VisibilityOff fontSize="small" />
                                                ) : (
                                                            <Visibility fontSize="small" />
                                                )}
                                            </div>
                                            <FormControl.Feedback type="invalid">
                                                <BsExclamationTriangleFill size={16} className="errorImage m-0" />
                                                <span className="font-sub errorText">Password is required</span>
                                            </FormControl.Feedback>
                                        </FormGroup>
                                        {loginFailureMessage
                                            && (
                                                <div className="d-flex justify-content-center">
                                                    <BsExclamationTriangleFill size={20} className="errorImage" />
                                                    <h5 className="font-main errorText">{loginFailureMessage}</h5>
                                                </div>
                                            )}
                                        {forceLogoutErrorMsg
                                            && (
                                                <div className="d-flex justify-content-center">
                                                    <BsExclamationTriangleFill size={12} className="errorImage" />
                                                    <h6 className="font-main errorText" style={{ fontSize: "12px" }}>{forceLogoutErrorMsg}</h6>
                                                </div>
                                            )}
                                        <Button
                                            variant="link"
                                            className="signOn-subText-Light"
                                            aria-label="Forgot password"
                                            onClick={() => {
                                                setForgotPasswordClicked(true);
                                                setModalWidthClass("modal-37w");
                                                dispatch(setResetPasswordSuccessMsg(""));
                                                dispatch(setForgotPasswordMsg(""));
                                            }}
                                        >
                                            Forgot Password?
                                        </Button>
                                    </>
                                )}
                                </Modal.Body>
                                <Modal.Footer className={`${showSignupModal ? 'border-0' : 'login-view-footer'}`}>
                                {showSignupModal && (
                                    <SignupFooter signIn={(success) => {
                                        setShowSignupModal(false);
                                        setModalWidthClass("");
                                        setLoginFailureMessage("");
                                        dispatch(setForceLogoutErrorMsg(null));
                                        if (success) {
                                            hideModal();
                                        }
                                    }}
                                    />
                                )}
                                {!showSignupModal && (
                                    <>
                                            <Container className="d-flex flex-row mb-3 p-0 m-0">
                                            <Container
                                                style={{
                                                    display: "flex",
                                                }}
                                                className="me-0 pe-0 justify-content-end"
                                            >
                                                <Button
                                                    className="secondary-button m-1"
                                                    variant="secondary"
                                                    onClick={() => {
                                                        dispatch(setAlreadySignUpErrorMsg(null));
                                                        dispatch(setResetPasswordSuccessMsg(""));
                                                        dispatch(setRedirectToMyAccount(false));
                                                        dispatch(setForceLogoutErrorMsg(null));
                                                        hideModal();
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    id="submitBtn"
                                                    disabled={isLoading || userDataLoading}
                                                    className={`order-now-card-button m-1 ${(isLoading || userDataLoading) ? 'disable-button' : ''}`}
                                                    type="submit"
                                                    onClick={(e) => {
                                                        handleSubmit(e);
                                                        dispatch(setResetPasswordSuccessMsg(""));
                                                    }}
                                                >
                                                    Sign In
                                                </Button>
                                            </Container>
                                            </Container>
                                            {!isEmbedMobileGiftCardTransfer && (
                                            <Container className="d-flex flex-column p-md-0 m-md-0">
                                                    <Button
                                                            className="tertiary-button w-100 m-mx-0 mb-2 mt-1"
                                                        variant="outline-secondary"
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                openSignUpModal();
                                                            }}
                                                            onKeyDown={(event) => {
                                                                if (event.key === "Enter" || event.key === " ") {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    openSignUpModal();
                                                                }
                                                            }}
                                                    >
                                                        Not a Member? Sign Up Today
                                                    </Button>
                                                    {showGuest && (
                                                        <Button
                                                            disabled={!!user || !(storeNumber !== '' || storeId !== '')}
                                                                className={`tertiary-button m-1 m-md-0 mb-1 mt-1 w-100 ${(user || !(storeNumber !== '' || storeId !== '')) ? 'disable-button' : ''}`}
                                                            variant="outline-dark"
                                                            type="submit"
                                                            // eslint-disable-next-line max-len
                                                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                                            onClick={handleGuest}
                                                        >
                                                            Continue as Guest
                                                        </Button>
                                                    )}
                                            </Container>
                                           )}
                                        {guestLoginFailureMessage && (
                                            <div className="pt-2 d-flex justify-content-center">
                                                <BsExclamationTriangleFill size={25} className="errorImage" />
                                                <h5 className="font-main errorText">{guestLoginFailureMessage}</h5>
                                            </div>
                                        )}
                                    </>
                                )}
                                </Modal.Footer>
                        </>
                )}
                </Modal>
            </Form>

        </div>
    );
};

export default LoginView;
